// frontend/src/utils/emailUtils.js

/**
 * Company email domain - only emails with this domain are considered valid work emails
 */
export const COMPANY_EMAIL_DOMAIN = 'winner-e.sg';

/** Classifications allowed to hold a non-company work_email (service / partner mailboxes). */
export const WORK_EMAIL_DOMAIN_EXEMPT = new Set(['system', 'outsourced']);

/**
 * Check if an email address is a valid work email (company domain)
 * @param {string} email - Email address to validate
 * @returns {boolean} - True if email is a valid work email
 */
export const isValidWorkEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return false;
  }

  const trimmedEmail = email.trim().toLowerCase();
  if (!trimmedEmail.includes('@')) {
    return false;
  }

  const domain = trimmedEmail.split('@')[1];
  return domain === COMPANY_EMAIL_DOMAIN;
};

/**
 * HR work-email policy error, or null when the value is acceptable.
 * Blank is always allowed (workers / staff without a mailbox → NULL).
 *
 * @param {string|null|undefined} email
 * @param {string|null|undefined} employmentClassification
 * @returns {string|null}
 */
export const workEmailDomainError = (email, employmentClassification = null) => {
  const trimmed = (email || '').trim();
  if (!trimmed) {
    return null;
  }
  if (WORK_EMAIL_DOMAIN_EXEMPT.has(employmentClassification || '')) {
    return null;
  }
  if (isValidWorkEmail(trimmed)) {
    return null;
  }
  return `Work email must use @${COMPANY_EMAIL_DOMAIN}. Personal addresses belong in Personal Email.`;
};

/**
 * Get safe work email for display (only returns valid work emails, null otherwise)
 * This prevents personal emails from being displayed in the Staff Directory
 * @param {object} employee - Employee object with work_email field
 * @returns {string|null} - Valid work email or null
 */
export const getSafeWorkEmail = (employee) => {
  if (!employee || !employee.work_email) {
    return null;
  }

  const email = employee.work_email.trim();
  if (!email) {
    return null;
  }

  // Only return email if it's a valid work email
  if (isValidWorkEmail(email)) {
    return email;
  }

  // Personal email - return null to hide it
  return null;
};
